import React, { useEffect } from 'react'
import { ApolloProvider } from '@apollo/client'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { Analytics } from '@/layout/Analytics'
import { ErrorBoundary, ErrorFallback } from '@/layout/ErrorBoundary'
import { DefaultSeo } from '@/layout/Seo'
import { LanguagesProvider } from '@/molecules/LanguageSelector'
import { ToastProvider } from '@/molecules/Toast/ToastProvider'
import { getWebClient } from '@/services/ApolloClient'
import { GuildProject, GuildProjectFlow, TorchProject } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { FilmmakerDashboardProvider } from '@/views/GuildCreateProjectsView/dashboard/context/FilmmakerDashboardContext'
import { DefaultLayoutProps } from '../DefaultLayout/DefaultLayout'
import { GuildProjectNav } from './navigation'

interface GuildProjectLayoutProps extends DefaultLayoutProps {
  title?: string
  projects?: GuildProject[]
  projectId?: string
  versionId?: string
  projectFlow?: GuildProjectFlow
  pendingInvites?: TorchProject[]
  isAdmin?: boolean
}

export const GuildProjectLayout: React.FC<GuildProjectLayoutProps> = ({
  children,
  isDarkMode = true,
  projects = [],
  pendingInvites,
  projectId,
  versionId,
  projectFlow,
  isAdmin,
}) => {
  const { query } = useSafeRouter()
  const { locale } = useLocale()
  const region = Array.isArray(query.region) ? query.region[0] : query.region
  const client = getWebClient({ locale, region: region?.length === 2 ? region : undefined })

  useEffect(() => {
    document.body.classList.add('!bg-gray-100')
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="p-4 lg:p-8">
        <DefaultSeo />
        <LanguagesProvider>
          <Analytics>
            <ApolloProvider client={client}>
              <FilmmakerDashboardProvider
                projects={projects}
                projectId={projectId as string}
                versionId={versionId as string}
                projectFlow={projectFlow}
                pendingInvites={pendingInvites}
                isAdmin={isAdmin}
              >
                <ThemeContextProvider isDarkMode={isDarkMode}>
                  <ToastProvider>
                    <GuildProjectNav />
                    <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
                  </ToastProvider>
                </ThemeContextProvider>
              </FilmmakerDashboardProvider>
            </ApolloProvider>
          </Analytics>
        </LanguagesProvider>
      </div>
    </ErrorBoundary>
  )
}
